<script setup>
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from "vue"
import { useHttpErrorsHandler } from "../../Composables/httpErrorsHandler"
import { useLocalStorage } from "../../Composables/useLocalStorage.js"
import { useDataManager } from "../../Composables/useDataManager.js"
import { useDefaultMessages } from "../../Composables/useDefaultMessages.js"
import { useAuthUserStore } from "../../Store/AuthUserStore.js"
import _ from 'lodash'
import DataTable from "../../Components/DataTable.vue"
import Modal from "../../Components/Modal.vue"

const { httpErrorsHandler } = useHttpErrorsHandler()
const authUserStore = useAuthUserStore()
const authUser = computed(() => authUserStore.authUser)

const storageKey = "Shiftech_DefaultMessageList"
const { saveToLocalStorage, loadFromLocalStorage } = useLocalStorage(storageKey)

const {
    url,
    data: defaultMessages,
    links,
    meta,
    loading,
    query,
    searchLoaded,
    sortingField,
    sortingFieldsOrder,
    changeSorting,
    search,
    loadData,
} = useDataManager({
    baseUrl: '/api/default-messages',
    baseSearchUrl: '/api/search/default-messages',
    defaultSortingField: 'created_at',
    defaultSortingFieldsOrder: {
        title: 'asc',
        created_at: 'asc',
    },
    onError: (error) => httpErrorsHandler(error),
})

const debouncedSearch = _.debounce(search, 250)

const columns = ref([
    { label: '#ID', field: 'id', slot: 'id', class: 'col-1' },
    { label: 'Titre', field: 'title', sortable: true, class: 'col-6' },
    { label: 'Rôles', slot: 'roles', class: 'col-1' },
    { label: 'Centres', slot: 'centers', class: 'col-1' },
    { label: 'Date création', field: 'created_at', sortable: true, slot: 'created_at', class: 'col-1' },
    { label: 'Détails', slot: 'details', class: 'col-1 text-center' },
    { label: 'Actions', slot: 'actions', class: 'col-1 text-center' },
])

onBeforeMount(() => {
    const savedSettings = loadFromLocalStorage()
    if (savedSettings) {
        url.value = savedSettings.url || url.value
        sortingField.value = savedSettings.sortingField || sortingField.value
        Object.assign(sortingFieldsOrder, savedSettings.sortingFieldsOrder || sortingFieldsOrder)
        query.value = savedSettings.query || ''
        searchLoaded.value = savedSettings.searchLoaded || false
    }

    loadData(url.value)
})

watch(
    [url, sortingField, sortingFieldsOrder, query, searchLoaded],
    ([newUrl, newSortingField, newSortingOrder, newQuery, newSearchLoaded]) => {
        saveToLocalStorage({
            url: newUrl,
            sortingField: newSortingField,
            sortingFieldsOrder: newSortingOrder,
            query: newQuery,
            searchLoaded: newSearchLoaded,
        })
    },
    { deep: true }
)

onBeforeUnmount(() => {
    document.querySelectorAll('.modal').forEach(modalElement => {
        const modalInstance = bootstrap.Modal.getInstance(modalElement)
        if (modalInstance) {
            modalInstance.hide()
        }
    })
})

const idToDelete = ref(null)
const messageToShow = ref(null)

function showModal(modalId, id = null) {
    if (modalId === 'modalDelete') {
        idToDelete.value = id
    }
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.show()
}

function hideModal(modalId) {
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.hide()
}

function showModalMessage(message) {
    messageToShow.value = message
    showModal('modalShowMessage')
}

const {
    deleteDefaultMessage,
} = useDefaultMessages({
    onDeleted: () => {
        loadData(url.value)
        hideModal('modalDelete')
    },
    onError: (error) => httpErrorsHandler(error),
})
</script>

<template>
    <div class="row">
        <div class="col-lg-12">
            <div class="card">
                <div class="card-header">
                    <div class="row g-3">
                        <div class="col-md-4">
                            <div class="search-box">
                                <input type="search" class="form-control search" placeholder="Rechercher..." v-model="query" @input="debouncedSearch" id="searchDefaultMessages">
                                <i class="ri-search-line search-icon"></i>
                            </div>
                        </div>
                        <div class="col-md-auto ms-auto">
                            <router-link :to="{ name:'default-messages.create' }" class="btn btn-secondary" v-if="authUser.can && authUser.can.create_default_messages">
                                <i class="ri-add-fill me-1 align-bottom"></i> Ajouter
                            </router-link>
                        </div>
                    </div>
                </div>
                <div class="card-body">
                    <data-table
                        :columns="columns"
                        :data="defaultMessages"
                        :loading="loading"
                        :links="links"
                        :meta="meta"
                        :sorting-field="sortingField"
                        :sorting-order="sortingFieldsOrder"
                        :search-loaded="searchLoaded"
                        @sort-changed="changeSorting"
                        @page-changed="loadData"
                    >
                        <!-- ID -->
                        <template #id="{ item }">
                            <span class="fw-medium text-dark">#{{ item.data.id }}</span>
                        </template>

                        <!-- Rôles -->
                        <template #roles="{ item }">
                            {{ item.data.allowed_roles === null ? 'Tous les utilisateurs' : 'Spécifiques' }}
                        </template>

                        <!-- Centres -->
                        <template #centers="{ item }">
                            {{ item.data.centers?.length === 0 ? 'Tous les centres' : 'Spécifiques' }}
                        </template>

                        <!-- Date création -->
                        <template #created_at="{ item }">
                            {{ item.data.created_at_fr }}
                        </template>

                        <!-- Détails -->
                        <template #details="{ item }">
                            <a href="#" class="btn btn-secondary bg-gradient waves-effect waves-light btn-sm" @click.prevent="showModalMessage(item)">
                                Accéder
                            </a>
                        </template>

                        <!-- Actions -->
                        <template #actions="{ item }">
                            <!-- UPDATE -->
                            <router-link :to="{ name: 'default-messages.edit', params: { id: item.data.id } }" class="text-info" title="Modifier le message" v-if="item.can.update">
                                <i class="ri-pencil-fill fs-17 px-1"></i>
                            </router-link>
                            <i class="ri-pencil-fill fs-17 px-1 text-muted" v-else></i>

                            <!-- DELETE -->
                            <a href="#" class="text-danger" title="Supprimer le message" @click.prevent="showModal('modalDelete', item.data.id)" v-if="item.can.delete">
                                <i class="ri-delete-bin-3-fill fs-17 px-1"></i>
                            </a>
                            <i class="ri-delete-bin-3-fill fs-17 px-1 text-muted" v-else></i>
                        </template>
                    </data-table>

                    <modal id="modalDelete" title="Supprimer le message prédéfini ?" btn-class="btn-danger" btn-text="Supprimer" @click:button="deleteDefaultMessage(idToDelete)">
                        <template #icon>
                            <lord-icon src="https://cdn.lordicon.com/gsqxdxog.json" trigger="loop" colors="primary:#212529,secondary:#f06548" style="width:90px;height:90px"></lord-icon>
                        </template>
                        <template #content>
                            <p class="text-muted fs-14 mb-4 pt-1">Vous êtes sur le point de supprimer un message prédéfini. Continuer ?</p>
                        </template>
                    </modal>

                    <!-- Modal show message -->
                    <div class="modal fade zoomIn" id="modalShowMessage" tabindex="-1" aria-hidden="true">
                        <div class="modal-dialog modal-xl">
                            <div class="modal-content" v-if="messageToShow !== null">
                                <div class="modal-header">
                                    <h5 class="modal-title">Contenu du message</h5>
                                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                                </div>
                                <div class="modal-body p-4">
                                    <p><span class="fw-bold">Titre :</span> {{ messageToShow.data.title }}</p>
                                    <p>
                                        <span class="fw-bold">Rôles :</span>
                                        <span v-if="messageToShow.data.allowed_roles === null">Tous les utilisateurs</span>
                                        <span v-else>
                                            <template v-for="(role, index) in messageToShow.data.allowed_roles" :key="index">
                                                <span class="badge fs-11 bg-dark-subtle ms-1 text-dark">{{ role }}</span>
                                            </template>
                                        </span>
                                    </p>
                                    <p>
                                        <span class="fw-bold">Centres :</span>
                                        <span v-if="messageToShow.data.centers?.length === 0">Tous les centres</span>
                                        <span v-else>
                                            <template v-for="(center, index) in messageToShow.data.centers" :key="index">
                                                <span class="badge fs-11 bg-secondary-subtle ms-1 mb-1 text-secondary">{{ center.data.name }}</span>
                                            </template>
                                        </span>
                                    </p>
                                    <div>
                                        <span class="fw-bold mb-3">Contenu :</span><br>
                                        <span v-html="messageToShow.data.body.replace(/\n/g, '<br />')"></span>
                                    </div>
                                    <div class="text-end mb-0">
                                        <button class="btn btn-soft-dark fw-medium text-decoration-none shadow-none" data-bs-dismiss="modal">Fermer</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>
